import type { Lesson } from '@yuan-math/curriculum';
import { insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/** The bridge from arithmetic to algebra — the point where letters arrive. */
export const ALGEBRA_LESSONS: Lesson[] = [
  lesson({
    id: 'variables-01',
    unitId: 'unit-intro-algebra',
    conceptId: 'variables',
    title: 'Variables',
    summary: 'A letter stands for a number we do not know yet — not for an object, and not for a unit.',
    objectives: ['Interpret a letter as an unknown number', 'Evaluate an expression for a given value', 'Write an expression from a description'],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'The box with a name',
        blocks: [
          text('You have solved problems like this for years:'),
          math('7 + \\square = 12'),
          text('The box holds a number you have to find. Algebra does the same thing, but gives the box a name so that you can talk about it, carry it through several steps, and use several boxes at once.'),
          math('7 + x = 12'),
        ],
      },
      intuition: {
        title: 'A letter is a number',
        blocks: [
          text('When you write 3a, the a is a number. If a is 4, then 3a is 12. The letter is a placeholder, and nothing more mysterious than that.'),
          warning('The commonest misunderstanding: reading "3a" as "3 apples". It is not 3 apples. If a is the number of apples, then 3a is three times however many apples there are — a number, not a fruit.', 'Read this carefully'),
          insight('Variables let you state a rule once instead of a thousand times. "The perimeter is 2(ℓ + w)" covers every rectangle that will ever exist.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          text('The same expression, evaluated at different values:'),
          table(['n', '2n + 3'], [['1', '5'], ['2', '7'], ['3', '9'], ['10', '23']]),
          text('The expression is a machine. Feed it a number, and it gives you one back.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Evaluate 4x − 7 when x = 5.'),
          steps([
            { label: 'Replace the letter with its value. Brackets keep it safe.', latex: '4(5) - 7' },
            { label: 'Multiplication before subtraction — the order of operations still applies.', latex: '20 - 7' },
            { label: 'Answer.', latex: '13' },
          ]),
          tip('Writing 4(5) rather than 45 is not fussiness. It is the habit that stops sign and precedence errors once the numbers get negative.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('variables', 4, 'Evaluate each expression for the value given.')] },
      independent: { title: 'On your own', blocks: [practice('variables', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('variables', 3, 'Write an expression from a description.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list(['A letter stands for a number.', '3a means 3 × a.', 'Substitute, then follow the order of operations.', 'A letter is never a label for an object.']),
        ],
      },
    },
  }),

  lesson({
    id: 'one-step-equations-01',
    unitId: 'unit-intro-algebra',
    conceptId: 'one-step-equations',
    title: 'One-Step Equations',
    summary: 'An equation is a balance. Whatever you do to one side, you must do to the other.',
    objectives: ['Solve equations using inverse operations', 'Explain why both sides must be treated the same', 'Check a solution by substitution'],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'The equals sign is a balance',
        blocks: [
          text('The equals sign does not mean "the answer is". It means "these two things are the same size".'),
          math('x + 4 = 11'),
          text('Picture a balance scale with x + 4 on one pan and 11 on the other. It is level. Your job is to get x alone on one pan without tipping it.'),
        ],
      },
      intuition: {
        title: 'Undo, on both sides',
        blocks: [
          text('To get x alone, remove the 4. But taking 4 off one pan tips the scale — so take 4 off the other pan too. The balance holds, and now x sits alone.'),
          math('x + 4 - 4 = 11 - 4 \\quad \\Rightarrow \\quad x = 7'),
          insight('You undo an operation with its inverse: subtraction undoes addition, division undoes multiplication. And whatever you do, you do to both sides. Those two rules solve every one-step equation there is.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          table(['To undo…', 'Do this', 'Example'], [
            ['+ 4', 'subtract 4', 'x + 4 = 11 → x = 7'],
            ['− 3', 'add 3', 'x − 3 = 5 → x = 8'],
            ['× 6', 'divide by 6', '6x = 42 → x = 7'],
            ['÷ 2', 'multiply by 2', 'x ÷ 2 = 9 → x = 18'],
          ]),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Solve 6x = 42.'),
          steps([
            { label: 'x is multiplied by 6, so divide by 6.', latex: '\\frac{6x}{6} = \\frac{42}{6}', note: 'Both sides, always.' },
            { label: 'Simplify.', latex: 'x = 7' },
            { label: 'Check by substituting back.', latex: '6 \\times 7 = 42 \;\\checkmark', note: 'The check is not optional — it is how you know.' },
          ]),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('one-step-equations', 4, 'Solve for x.')] },
      independent: { title: 'On your own', blocks: [practice('one-step-equations', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('one-step-equations', 3)] },
      review: {
        title: 'What to remember',
        blocks: [
          list(['An equation is a balance, not an instruction.', 'Undo with the inverse operation.', 'Do the same thing to both sides.', 'Always check by substituting your answer back.']),
          insight('Every equation you will ever solve — quadratics, systems, differential equations — uses these same two rules. They do not get replaced; they get repeated.'),
        ],
      },
    },
  }),
];
